/**
 * components/ContactFormModal.js
 * Create / edit a normal Contact (Kontak page) — Contact model redesign, stage 2 (the form
 * rebuild). `initial` is null on create, the contact object on edit. Reused for both.
 *
 * Collects every real field on the new Contact shape: `list`, `name`, `personIds`, `addresses`,
 * `city`, `phone`, `contactPerson`, `email`, `keterangan`, `npwp`, `pkp`, `labels`, `ekspedisi`,
 * `deactivated`. `businessId` is deliberately NOT in this form — business linking has no UI yet,
 * later work.
 *
 * No silent defaults: `list` starts unset on create (a two-button toggle, neither pressed, so a
 * value only ever appears once the user deliberately picks one). `personIds` starts EMPTY on
 * create, even when `persons` has exactly one entry. An address's "Default" checkbox is
 * exclusive across rows and can be unchecked back to none — removing the default row never
 * promotes another. **The one exception (default-address rule, `utils/contacts.js`'s own
 * `normAddresses`): with exactly ONE address, that address is always saved as default — its own
 * checkbox renders checked and disabled, with a note explaining why, so the screen states the
 * true save outcome instead of a save silently reversing an unchecked box with no visible sign.**
 * The checkbox becomes interactive again the moment a second address exists.
 *
 * **`list` is fixed at creation, never editable afterwards** (`utils/contacts.js`'s own header
 * comment has the full reasoning — mirrors a salesperson role pinned to its branch for life). In
 * EDIT mode `list` renders as plain read-only text, not a control the user might try to click;
 * only CREATE mode shows the picking toggle. `updateContact` enforces this independently on the
 * write path too, so this is presentation only, not the real gate.
 *
 * Has NO duplicate-name check of its own (removed — it used to be non-list-scoped, so it
 * misfired against every internal contact's shared placeholder name). `utils/contacts.js`'s
 * `validateContactFields` is the single source of truth; `onSave` returns `{ok, error}` and the
 * caller (`KontakPage`) keeps this modal open and passes the real `error` back on a rejected
 * save.
 *
 * **A person that disappears from the live `persons` prop while checked is never silently
 * dropped from `personIds` at submit** — that would save a set of people different from what
 * the form displayed, with no sign anything changed (the exact pattern this project already
 * removed from the salesperson pre-fill). Instead it's tracked by name (via a small sticky
 * cache, since the live `persons` array itself no longer has the name to show) and surfaced as
 * a blocking red row the user must resolve — uncheck it, then pick someone else if needed —
 * before Simpan/Tambah re-enables.
 *
 * **Never a `<form>` element** (bug fix — this used to be one): `ContactPicker`'s own full-form
 * create flow (below) nests this modal inside whatever form the host is already rendering
 * (e.g. `TransactionFormModal`'s own `<form>`). A `<form>` nested inside another `<form>` is
 * invalid HTML, and clicking this modal's own submit button fired a native "submit" event that
 * bubbled (both natively and through React's own synthetic event simulation, which follows the
 * REACT tree regardless of DOM placement) into the OUTER form's `onSubmit` handler too — in a
 * real browser this manifested as the outer form performing its own default submission
 * (navigating the whole SPA away, discarding everything) the instant the inner "Tambah" was
 * clicked, before this modal's own `onSave` had a chance to matter. The fix is structural, not a
 * `stopPropagation()` patch: this modal is a plain `<div>` with its own `onClick`-driven Simpan/
 * Tambah button, exactly mirroring `SalespersonFormModal`'s own established pattern for a modal
 * that might be nested inside another form (that component was never a `<form>` either, for the
 * same reason). One accepted trade-off, matching that same precedent: pressing Enter in a text
 * field no longer submits — this component has never relied on that for any of its own tests.
 *
 * **`fixedList`/`initialName`** (ContactPicker's own full-form create flow, Part 1 of the
 * contacts/salespeople link-up): an optional pair used ONLY when this modal is opened from
 * `ContactPicker`'s "+ Tambah kontak baru" inside a form that already has a genuine location
 * anchor (e.g. `TransactionFormModal`'s own `location`). `fixedList` seeds `form.list` and,
 * exactly like edit mode, renders it as plain read-only text instead of the Pusat/Cabang
 * toggle — the list is a fact of WHERE this contact is being created from, not a choice being
 * offered here, and the user must still be able to SEE it. `initialName` seeds `form.name` from
 * whatever the picker's own combobox already had typed, so the user isn't asked to retype it.
 * Both are create-mode-only concerns — `initial` (a real persisted contact) always wins when
 * present, same precedence a real value always gets over a seed default elsewhere in this app.
 */
import React, { useEffect, useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { displayPerson } from "../utils/persons";

const LABEL_OPTIONS = [
  { value: "supplier", label: "Supplier" },
  { value: "customer", label: "Pelanggan" },
  { value: "broker", label: "Broker" },
];

export default function ContactFormModal({
  initial, persons = [], error = null, fixedList = null, initialName = "", onSave, onClose,
}) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(() => ({
    list: initial?.list || fixedList || "",
    name: initial?.name || initialName || "",
    personIds: initial?.personIds ? [...initial.personIds] : [],
    addresses: initial?.addresses ? initial.addresses.map((a) => ({ ...a })) : [],
    city: initial?.city || "",
    phone: initial?.phone || "",
    contactPerson: initial?.contactPerson || "",
    email: initial?.email || "",
    keterangan: initial?.keterangan || "",
    npwp: initial?.npwp || "",
    pkp: initial?.pkp === true,
    labels: initial?.labels ? [...initial.labels] : [],
    ekspedisi: initial?.ekspedisi ? [...initial.ekspedisi] : [],
    deactivated: initial?.deactivated === true,
  }));

  // A sticky id -> {name, code} cache, merged from every `persons` prop this form has ever
  // seen (never removes an entry) — so a person who disappears from the LIVE list while this
  // form stays open can still be named in the "no longer available" message below, instead of
  // silently vanishing into an unreadable id.
  const [personCache, setPersonCache] = useState(() => {
    const m = {};
    for (const p of persons) m[p.id] = { name: p.name, code: p.code };
    return m;
  });
  useEffect(() => {
    setPersonCache((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const p of persons) {
        if (!next[p.id] || next[p.id].name !== p.name || next[p.id].code !== p.code) {
          next[p.id] = { name: p.name, code: p.code };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [persons]);
  const personLabel = (id) => {
    const cached = personCache[id];
    return cached ? `${cached.name}${cached.code ? ` (${cached.code})` : ""}` : id;
  };

  useModalEsc(onClose);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePerson = (id) =>
    setForm((f) => ({
      ...f,
      personIds: f.personIds.includes(id) ? f.personIds.filter((x) => x !== id) : [...f.personIds, id],
    }));

  const toggleLabel = (value) =>
    setForm((f) => ({
      ...f,
      labels: f.labels.includes(value) ? f.labels.filter((x) => x !== value) : [...f.labels, value],
    }));

  const addAddress = () => setForm((f) => ({ ...f, addresses: [...f.addresses, { text: "", isDefault: false }] }));
  const removeAddress = (i) => setForm((f) => ({ ...f, addresses: f.addresses.filter((_, idx) => idx !== i) }));
  const setAddressText = (i, text) =>
    setForm((f) => ({ ...f, addresses: f.addresses.map((a, idx) => (idx === i ? { ...a, text } : a)) }));
  // Exclusive across rows, and toggles back OFF on its own row — clicking the currently-default
  // row's checkbox unmarks it (net: none default), clicking a different row marks that one and
  // clears every other. Never auto-promotes anything — see the file header's "no silent
  // defaults" note and the "default address deleted" edge case (removeAddress just drops the
  // row; nothing here runs to pick a replacement). Unreachable with exactly one address — that
  // row's own checkbox renders `disabled` (below), since the default-address rule decides it
  // unconditionally; this function only ever runs again once a second address exists.
  const toggleAddressDefault = (i) =>
    setForm((f) => {
      const wasDefault = f.addresses[i].isDefault === true;
      return { ...f, addresses: f.addresses.map((a, idx) => ({ ...a, isDefault: idx === i ? !wasDefault : false })) };
    });

  const addEkspedisi = () => setForm((f) => ({ ...f, ekspedisi: [...f.ekspedisi, ""] }));
  const removeEkspedisi = (i) => setForm((f) => ({ ...f, ekspedisi: f.ekspedisi.filter((_, idx) => idx !== i) }));
  const setEkspedisiName = (i, name) =>
    setForm((f) => ({ ...f, ekspedisi: f.ekspedisi.map((e, idx) => (idx === i ? name : e)) }));

  // Ids the form currently has checked that no longer resolve against the LIVE persons prop —
  // never silently dropped (see file header). Submit stays blocked while any exist; the user
  // must uncheck them (a deliberate action, shown by name via personCache) before saving.
  const missingSelected = form.personIds.filter((id) => !persons.some((p) => p.id === id));
  const resolvedPersonIds = form.personIds.filter((id) => persons.some((p) => p.id === id));
  const listValid = form.list === "PUSAT" || form.list === "CABANG";
  const nameValid = form.name.trim().length > 0;
  const canSubmit = listValid && nameValid && resolvedPersonIds.length > 0 && missingSelected.length === 0;

  // A plain function, not a form submit handler — see the file header's own note on why this
  // component is a `<div>`, never a `<form>`.
  const submit = () => {
    if (!canSubmit) return;
    const payload = {
      name: form.name.trim(),
      personIds: form.personIds,
      addresses: form.addresses
        .map((a) => ({ id: a.id, text: a.text.trim(), isDefault: a.isDefault === true }))
        .filter((a) => a.text.length > 0),
      city: form.city,
      phone: form.phone,
      contactPerson: form.contactPerson,
      email: form.email,
      keterangan: form.keterangan,
      npwp: form.npwp,
      pkp: form.pkp === true,
      labels: form.labels,
      ekspedisi: form.ekspedisi.map((e) => e.trim()).filter(Boolean),
      deactivated: form.deactivated === true,
    };
    // list is fixed at creation (utils/contacts.js) — only ever sent on CREATE, where it's a
    // real deliberate pick; an edit never sends it at all, so there's nothing for updateContact
    // to even compare against (defense in depth alongside that function's own write-path guard).
    if (!isEdit) payload.list = form.list;
    onSave(payload);
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box modal-box--wide" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">{isEdit ? "Ubah Kontak" : "Tambah Kontak"}</h2>

        {error && (
          <div className="form-hint" style={{ color: "var(--red)", marginBottom: "var(--gap-md)" }}>{error}</div>
        )}

        <div className="form-grid">
          <div className="form-row">
            <label className="form-label">Daftar</label>
            {isEdit || fixedList ? (
              // Fixed at creation — plain read-only text, never a control the user might try to
              // click (see the file header). A contact that belongs in the other list is never
              // "moved"; a new entry is created there instead. `fixedList` (create mode, opened
              // from a host with a genuine location anchor) reads the same way: the list is
              // already decided by that anchor, still visibly stated, just not a choice here.
              <div>{form.list === "PUSAT" ? "Pusat" : "Cabang"}</div>
            ) : (
              <>
                <div className="seg">
                  <button type="button" className={form.list === "PUSAT" ? "active" : ""}
                    onClick={() => set("list", "PUSAT")}>Pusat</button>
                  <button type="button" className={form.list === "CABANG" ? "active" : ""}
                    onClick={() => set("list", "CABANG")}>Cabang</button>
                </div>
                {!listValid && <div className="form-hint" style={{ color: "var(--red)" }}>Pilih Pusat atau Cabang.</div>}
              </>
            )}
          </div>

          <div className="form-row">
            <label className="form-label" htmlFor="contact-form-nama">Nama</label>
            <input id="contact-form-nama" className="form-input" value={form.name} autoFocus
              onChange={(e) => set("name", e.target.value)} />
            {/* Disabled-control sweep (manual-testing report) — shared convention: matches the
                "Wajib diisi." this form already shows for Sales/Person and Daftar. */}
            {!form.name.trim() && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
          </div>
        </div>

        <div className="form-row">
          <label className="form-label">Sales / Person</label>
          {missingSelected.length > 0 && (
            <div className="form-hint" style={{ color: "var(--red)", marginBottom: "var(--gap-sm)" }}>
              {missingSelected.map(personLabel).join(", ")} sudah tidak tersedia (mungkin
              dihapus) — hapus centangnya lalu pilih ulang sebelum menyimpan.
            </div>
          )}
          {persons.length === 0 && missingSelected.length === 0 ? (
            <div className="form-hint">Belum ada data sales/person.</div>
          ) : (
            <div className="kontak-form-scrollbox">
              {missingSelected.map((id) => (
                <label key={id} style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", padding: "3px 0", color: "var(--red)" }}>
                  <input type="checkbox" checked={true} onChange={() => togglePerson(id)} />
                  <span>{personLabel(id)} — tidak tersedia lagi</span>
                </label>
              ))}
              {persons.map((p) => (
                <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", padding: "3px 0" }}>
                  <input type="checkbox" checked={form.personIds.includes(p.id)}
                    onChange={() => togglePerson(p.id)} />
                  <span>{displayPerson(p)}{p.code ? ` (${p.code})` : ""}</span>
                </label>
              ))}
            </div>
          )}
          {resolvedPersonIds.length === 0 && missingSelected.length === 0 && (
            <div className="form-hint" style={{ color: "var(--red)" }}>Pilih minimal satu sales/person.</div>
          )}
        </div>

        <div className="form-row">
          <label className="form-label">Alamat</label>
          {form.addresses.length === 0 && (
            <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>Belum ada alamat.</div>
          )}
          {form.addresses.map((a, i) => {
            // Default-address rule (`utils/contacts.js`'s own `normAddresses`): a lone address
            // is ALWAYS saved as default, regardless of this checkbox's stored state — unticking
            // it here used to look like a real choice while the save silently forced it back,
            // with nothing telling the user. Rendered checked+disabled instead, so the screen
            // states the true outcome up front rather than reversing a visible action.
            const isOnlyAddress = form.addresses.length === 1;
            return (
              <div className="kontak-form-row" key={a.id ?? i}>
                <input className="form-input" style={{ flex: 1 }} value={a.text} placeholder="Alamat"
                  onChange={(e) => setAddressText(i, e.target.value)} />
                <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-xs)", whiteSpace: "nowrap", fontSize: 13, marginTop: "var(--gap-sm)" }}>
                  <input type="checkbox" checked={isOnlyAddress || a.isDefault === true} disabled={isOnlyAddress}
                    onChange={() => toggleAddressDefault(i)} />
                  Default
                </label>
                <button type="button" className="btn btn-ghost btn-sm" title="Hapus alamat"
                  onClick={() => removeAddress(i)}>✕</button>
              </div>
            );
          })}
          {form.addresses.length === 1 && (
            <div className="form-hint">Alamat satu-satunya otomatis jadi default.</div>
          )}
          <button type="button" className="btn btn-sm" onClick={addAddress}>+ Tambah Alamat</button>
        </div>

        <div className="form-grid">
          <div className="form-row">
            <label className="form-label" htmlFor="contact-form-kota">Kota</label>
            <input id="contact-form-kota" className="form-input" value={form.city}
              onChange={(e) => set("city", e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="contact-form-telepon">Telepon</label>
            <input id="contact-form-telepon" className="form-input" value={form.phone}
              onChange={(e) => set("phone", e.target.value)} />
          </div>
        </div>

        <div className="form-grid">
          <div className="form-row">
            <label className="form-label" htmlFor="contact-form-cp">Contact Person</label>
            <input id="contact-form-cp" className="form-input" value={form.contactPerson}
              onChange={(e) => set("contactPerson", e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="contact-form-email">Email</label>
            <input id="contact-form-email" className="form-input" type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)} />
          </div>
        </div>

        <div className="form-grid">
          <div className="form-row">
            <label className="form-label" htmlFor="contact-form-npwp">NPWP</label>
            <input id="contact-form-npwp" className="form-input" value={form.npwp}
              onChange={(e) => set("npwp", e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">&nbsp;</label>
            <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)" }}>
              <input type="checkbox" checked={form.pkp} onChange={(e) => set("pkp", e.target.checked)} />
              PKP (Pengusaha Kena Pajak)
            </label>
          </div>
        </div>

        <div className="form-row">
          <label className="form-label">Label</label>
          <div style={{ display: "flex", gap: "var(--gap-lg)" }}>
            {LABEL_OPTIONS.map((opt) => (
              <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)" }}>
                <input type="checkbox" checked={form.labels.includes(opt.value)}
                  onChange={() => toggleLabel(opt.value)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div className="form-row">
          <label className="form-label">Ekspedisi</label>
          {form.ekspedisi.length === 0 && (
            <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>Belum ada ekspedisi.</div>
          )}
          {form.ekspedisi.map((name, i) => (
            <div className="kontak-form-row" key={i}>
              <input className="form-input" style={{ flex: 1 }} value={name} placeholder="Nama ekspedisi"
                onChange={(e) => setEkspedisiName(i, e.target.value)} />
              <button type="button" className="btn btn-ghost btn-sm" title="Hapus ekspedisi"
                onClick={() => removeEkspedisi(i)}>✕</button>
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={addEkspedisi}>+ Tambah Ekspedisi</button>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="contact-form-keterangan">Keterangan</label>
          <textarea id="contact-form-keterangan" className="form-input" value={form.keterangan}
            onChange={(e) => set("keterangan", e.target.value)} />
        </div>

        {isEdit && !initial?.internal && (
          <div className="form-row">
            <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)" }}>
              <input type="checkbox" checked={form.deactivated}
                onChange={(e) => set("deactivated", e.target.checked)} />
              Nonaktifkan kontak ini
            </label>
            <div className="form-hint">
              Kontak nonaktif tidak muncul lagi di pilihan transaksi baru — riwayat, transaksi
              lama, dan data lain tetap ada, dan kontak ini bisa diaktifkan kembali kapan saja.
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={submit}>
            {isEdit ? "Simpan" : "Tambah"}
          </button>
        </div>
      </div>
    </div>
  );
}
